import { Money, MoneyCurrency } from '@booking-core/domain/value_objects/money.js';
import { InvariantViolation } from '@shared-kernel/domain-error.js';
import type {
  PricingInput,
  PricingStrategy,
} from '../pricing-strategy.js';

export interface PackageBlockParams {
  /** First-block fixed price (e.g. NT$300 for first 60 min). */
  readonly firstBlockMinutes: number;
  readonly firstBlockAmount: number;
  /** Per-additional-block rate (e.g. NT$100 per extra 30 min). */
  readonly additionalBlockMinutes: number;
  readonly additionalBlockAmount: number;
  /** Optional: scale by partySize (default false — flat for whole party). */
  readonly multiplyByPartySize?: boolean;
}

/**
 * 套裝計費：首 block 固定 + 加時 block 比例。
 *
 *   min ≤ firstBlockMinutes → firstBlockAmount
 *   else                    → firstBlockAmount + ceil(extra/addBlock) × addAmount
 *
 * multiplyByPartySize=true → 按人頭放大整筆。
 *
 * 兩邊都用（user 決定 booking_core 也開放 package_block）：
 *   - reservation 用「首小時 × 預訂時窗」做事前報價
 *   - play session 用「首小時 × billable duration (after rounding)」做事後計費
 */
export class PackageBlockStrategy implements PricingStrategy {
  constructor(private readonly params: PackageBlockParams) {
    if (params.firstBlockMinutes <= 0 || params.additionalBlockMinutes <= 0) {
      throw new InvariantViolation('PackageBlockStrategy: block sizes must be > 0');
    }
  }

  quote(input: PricingInput): Money {
    if (input.partySize <= 0) {
      return Money.zero(MoneyCurrency.TWD);
    }
    let amount = this.params.firstBlockAmount;
    const extra = input.billableDurationMinutes - this.params.firstBlockMinutes;
    if (extra > 0) {
      const extraBlocks = Math.ceil(extra / this.params.additionalBlockMinutes);
      amount += extraBlocks * this.params.additionalBlockAmount;
    }
    if (this.params.multiplyByPartySize) {
      amount *= input.partySize;
    }
    return Money.of({ amount, currency: MoneyCurrency.TWD });
  }
}
